'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useDeviceId } from '../hooks/useDeviceId';
import { upsertAnonymousUser } from '../../lib/tracking';

interface TrackingContextValue {
  deviceId: string | null;
}

const TrackingContext = createContext<TrackingContextValue>({ deviceId: null });

export function useTracking() {
  return useContext(TrackingContext);
}

interface TrackingProviderProps {
  children: ReactNode;
}

/**
 * Provider component that initializes anonymous user tracking.
 * Wraps the app and provides deviceId to child components via context.
 */
export function TrackingProvider({ children }: TrackingProviderProps) {
  const deviceId = useDeviceId();

  // Upsert anonymous user on mount (when deviceId is available)
  useEffect(() => {
    if (deviceId) {
      upsertAnonymousUser(deviceId);
    }
  }, [deviceId]);

  return (
    <TrackingContext.Provider value={{ deviceId }}>
      {children}
    </TrackingContext.Provider>
  );
}
